// Environment-gated logger.
//
// In development: prints to the browser console.
// In every environment: forwards errors to the self-hosted error endpoint
// (/api/log/error) so a real inbox alert fires for handled catch blocks
// too — not just uncaught crashes.
//
// Every existing `logger.error("Something failed", err)` call across the app
// now:
//   • Prints in dev (as before), silent in prod
//   • Sends a deduped POST to the backend so the ERROR_ALERT_EMAIL wired in
//     backend/routers/error_logs.py fires within seconds
//
// Kept the same shape as before so no other files need editing.
import { reportClientError } from "./errorReporter";

const isDev = process.env.NODE_ENV !== "production";

// Convert (msg, err?) or (err) into a { message, stack } tuple the reporter
// can consume. Non-Error second args (strings, numbers) get appended to the
// primary message so we don't lose context.
const extract = (args) => {
  if (!args || args.length === 0) return { message: "", stack: null };
  let message = "";
  let stack = null;
  for (const a of args) {
    if (a instanceof Error) {
      message = message ? `${message} — ${a.message}` : a.message;
      stack = stack || a.stack || null;
    } else if (typeof a === "string") {
      message = message ? `${message} ${a}` : a;
    } else if (a && typeof a === "object" && a.message) {
      message = message ? `${message} — ${a.message}` : a.message;
      stack = stack || a.stack || null;
    } else if (a !== undefined) {
      try {
        message = message ? `${message} ${JSON.stringify(a)}` : String(a);
      } catch {
        message = message ? `${message} [unserializable]` : "[unserializable]";
      }
    }
  }
  return { message: message.slice(0, 2048), stack };
};

const logger = {
  error: (...args) => {
    if (isDev) console.error(...args);
    // Fire-and-forget report — errorReporter internally dedupes and
    // swallows any failures so this can't cascade.
    try {
      const { message, stack } = extract(args);
      if (message) reportClientError(`[handled] ${message}`, stack);
    } catch { /* never let the logger throw */ }
  },
  warn: (...args) => { if (isDev) console.warn(...args); },
  info: (...args) => { if (isDev) console.info(...args); },
};

export default logger;
